import { useState, type FormEvent } from 'react'
import { Database, ShieldCheck } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Field, Input } from '@/components/ui/input'
import { ErrorNote } from '@/components/ui/misc'
import { ApiError } from '@/lib/api'
import { useAuth } from './AuthProvider'

export function LoginPage() {
  const { login } = useAuth()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [errorCode, setErrorCode] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setErrorCode(null)
    setBusy(true)
    try {
      await login(username, password)
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message)
        setErrorCode(err.code)
      } else {
        setError('Could not sign in. Please try again.')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm space-y-6">
        <div className="space-y-2 text-center">
          <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-lg bg-accent/12">
            <Database className="h-5 w-5 text-accent" />
          </div>
          <h1 className="text-xl font-semibold">public-s3-service</h1>
          <p className="text-sm text-muted-foreground">
            Sign in to browse Hitachi Content Platform storage.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="space-y-4 rounded-lg border border-border bg-surface p-5"
        >
          {error && (
            <ErrorNote>
              {error}
              {errorCode === 'NO_APP_ROLE' && (
                <span className="mt-1 block text-xs">
                  Your account signed in successfully but holds no role permitting access here.
                  An administrator needs to grant one.
                </span>
              )}
              {errorCode === 'AUTH_SERVER_UNREACHABLE' && (
                <span className="mt-1 block text-xs">
                  This is a connectivity problem, not a password problem.
                </span>
              )}
            </ErrorNote>
          )}

          <Field label="Username" htmlFor="username">
            <Input
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoFocus
              required
            />
          </Field>

          <Field label="Password" htmlFor="password">
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </Field>

          <Button type="submit" variant="primary" className="w-full justify-center" loading={busy}>
            Sign in
          </Button>
        </form>

        <div className="space-y-2 text-center text-xs text-muted-foreground">
          <p className="flex items-center justify-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5" />
            Signed-in actions are attributed to your account.
          </p>
          <p>Actions are recorded against your account in the audit log.</p>
        </div>
      </div>
    </div>
  )
}
